import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  info: string | null;
}

/**
 * Captura erros de renderização para que uma falha vire uma tela explicativa,
 * e não uma página em branco.
 *
 * Sem isto, um `undefined` inesperado vindo da API derruba a árvore inteira do
 * React: o usuário vê o app sumir, o servidor não registra nada e a única
 * pista fica no console do navegador, já minificada.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // O componentStack aponta o componente culpado mesmo com o build minificado.
    console.error('[Allodos] Erro de renderização:', error, info.componentStack);
    this.setState({ info: info.componentStack ?? null });
  }

  private handleReload = () => window.location.reload();

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6 font-sans">
        <div className="w-full max-w-xl bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-2xl p-5 sm:p-10 space-y-6">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-rose-500">
              Algo deu errado
            </p>
            <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight mt-2">
              Não foi possível exibir esta tela
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mt-3 leading-relaxed">
              Seus dados estão salvos. Recarregue a página; se o erro continuar,
              envie os detalhes abaixo para o suporte.
            </p>
          </div>

          <details className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
            <summary className="text-[10px] font-black uppercase tracking-widest text-slate-500 cursor-pointer">
              Detalhes técnicos
            </summary>
            <pre className="mt-4 text-[11px] leading-relaxed text-rose-600 dark:text-rose-300 whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
              {error.message}
              {info ? `\n${info}` : ''}
            </pre>
          </details>

          <div className="flex gap-3">
            <button
              onClick={this.handleReload}
              className="flex-1 p-4 bg-indigo-600 text-white rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-indigo-700 transition-all shadow-lg"
            >
              Recarregar
            </button>
            <a
              href="https://wa.me/5554981115362?text=Erro%20no%20Allodos"
              target="_blank"
              rel="noreferrer"
              className="flex-1 p-4 border border-slate-200 dark:border-slate-700 rounded-2xl font-black uppercase text-xs tracking-widest text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all text-center"
            >
              Falar com o suporte
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
